import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction, Link, useSearchParams } from '@remix-run/react'
import { ArrowLeftIcon } from 'lucide-react'
import { Button } from '#app/components/ui/button'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/terms' }],
}

export const meta: MetaFunction = () => [
	{ title: `Terms and Conditions | Erda` },
	{
		name: 'description',
		content: `Terms and Conditions for Erda.app`,
	},
	{
		name: 'keywords',
		content:
			'terms, conditions, terms of service, terms of use, terms and conditions, terms of service, terms of use, terms and conditions, terms of service, terms of use, terms and conditions',
	},
	{ name: 'og:title', content: `Terms | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/terms.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Terms and Conditions for Erda.app`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/terms.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Terms | Erda` },
	{
		name: 'twitter:description',
		content: `Terms and Conditions for Erda.app`,
	},
]

export default function Terms() {
	const [searchParams] = useSearchParams()
	const from = searchParams.get('from')
	const fromLabel = from ? from.charAt(1).toUpperCase() + from.slice(2) : null

	return (
		<>
			{from ? (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to={from}>
						<ArrowLeftIcon className="h-4 w-4" /> {fromLabel}
					</Link>
				</Button>
			) : (
				<Button
					data-vaul-no-drag
					variant="link"
					className="h-auto w-fit self-center"
					asChild
				>
					<Link to="/info">
						<ArrowLeftIcon className="h-4 w-4" /> Info hub
					</Link>
				</Button>
			)}
			<div className="panel-copy space-y-4 text-sm">
				<h1 className="text-base font-bold">Terms and Conditions</h1>
				<p className="!mt-0 text-muted-foreground">
					Last updated: 11th March 2024
				</p>
				<p>
					Welcome to Erda (the "Website"), located at{' '}
					<a href="https://erda.app" className="underline">
						https://erda.app
					</a>
					. The Website is operated by Moorst Ltd. ("we", "us", or "our"). By
					accessing or using our Website, you ("User" or "you") agree to be
					bound by these Terms and Conditions ("Terms"). If you disagree with
					any part of these Terms, you must not use our Website.
				</p>
				<h2 className="font-bold">1. Introduction</h2>
				<p className="!mt-0">
					Erda lets each verified account claim one available digital place for
					free, with no annual fee or required donation. Additional places and
					gifts cost $2 per place per year and renew annually until cancelled.
					Existing paid subscriptions remain unchanged. Ownership is exclusive
					on Erda’s digital map and includes no physical land or property
					rights. A free claim does not fund trees. An optional one-off $2
					contribution helps fund two trees through Trees for the Future and
					does not create a subscription or affect your ownership.
				</p>
				<h2 className="font-bold">2. Intellectual Property Rights</h2>
				<p className="!mt-0">
					Unless otherwise stated, we or our licensors own the intellectual
					property rights for all material on Erda. All intellectual property
					rights are reserved.
				</p>
				<h2 className="font-bold">3. User Responsibilities</h2>
				<p className="!mt-0">
					You are responsible for your account and any activity associated with
					it. You must safeguard your account credentials and notify us
					immediately of any unauthorized use of your account.
				</p>
				<h2 className="font-bold">4. Account Information</h2>
				<p className="!mt-0">
					When creating and maintaining your account on Erda, you may provide
					certain personal information. This may include but is not limited to a
					handle, email address, name and other information that helps us
					provide our services. We do not store passwords directly;
					authentication is managed through OTPs or third-party services like
					Google authentication. You may request to delete your account and
					associated data at any time, please contact us at{' '}
					<Link to="mailto:support@erda.app" className="underline">
						support@erda.app
					</Link>{' '}
					to do so.
				</p>
				<h2 className="font-bold">5. User Content Policy</h2>
				<p className="!mt-0">
					You are responsible for the content you post on your profile and owned
					Places. While we do not actively monitor all content, we reserve the
					right to remove any content deemed inappropriate or offensive. We rely
					on our community to report content that violates our policies.
				</p>
				<h2 className="font-bold">6. Payments</h2>
				<p className="!mt-0">
					Transactions on Erda are processed via Stripe. We do not store your
					financial details. Due to our commitment to supporting environmental
					causes, a portion of each transaction is donated to tree planting
					initiatives, and refunds are not available.
				</p>
				<h2 className="font-bold">7. Termination</h2>
				<p className="!mt-0">
					We may terminate or suspend your access to the Website for any breach
					of these Terms without prior notice or liability.
				</p>
				<h2 className="font-bold">8. Limitation of Liability</h2>
				<p className="!mt-0"></p>Our liability is limited to the maximum extent
				permitted by law. We are not liable for any direct, indirect,
				incidental, or consequential damages arising from your use of the
				Website.
				<h2 className="font-bold">9. Governing Law</h2>
				<p className="!mt-0">
					These Terms are governed by the laws of the United Kingdom, without
					regard to its conflict of law provisions.
				</p>
				<h2 className="font-bold">10. Changes to Terms</h2>
				<p className="!mt-0">
					We reserve the right to modify these Terms at any time. By continuing
					to use the Website after changes are made, you agree to be bound by
					the revised terms.
				</p>
				<h2 className="font-bold">11. Contact Information</h2>
				<p className="!mt-0">
					If you have any questions about these Terms, please contact us at
					<Link to="mailto:support@erda.app" className="underline">
						support@erda.app
					</Link>
					.
				</p>
			</div>
		</>
	)
}
